/**
 * LUXIRE COLLECTION FILTER ENGINE v2
 * Tag-based product filtering for Luxire collection pages.
 */
(function () {
  'use strict';

  const FILTER_CONFIG = {
    color: {
      label: 'Color', type: 'color',
      order: ['white','black','grey','navy','blue','brown','green','red','pink','purple','yellow','orange','beige','burgundy'],
      display: {
        white:{label:'White / Cream',hex:'#f5f5f0',border:true},black:{label:'Black',hex:'#1a1a1a'},grey:{label:'Grey',hex:'#6b7280'},navy:{label:'Navy',hex:'#1e3a5f'},blue:{label:'Blue',hex:'#3b82f6'},brown:{label:'Brown / Tan',hex:'#92400e'},green:{label:'Green',hex:'#166534'},red:{label:'Red',hex:'#b91c1c'},pink:{label:'Pink',hex:'#ec4899'},purple:{label:'Purple',hex:'#7c3aed'},yellow:{label:'Yellow / Gold',hex:'#ca8a04'},orange:{label:'Orange',hex:'#ea580c'},beige:{label:'Beige / Khaki',hex:'#d4b896'},burgundy:{label:'Burgundy / Wine',hex:'#722F37'}
      },
      groups: {
        white:{tags:['white','off-white']},black:{tags:['black']},grey:{tags:['grey','charcoal']},navy:{tags:['navy']},blue:{tags:['blue','light-blue','royal-blue','teal']},brown:{tags:['brown','tan']},green:{tags:['green','olive']},red:{tags:['red']},pink:{tags:['pink']},purple:{tags:['purple']},yellow:{tags:['yellow']},orange:{tags:['orange']},beige:{tags:['khaki']},burgundy:{tags:['burgundy']}
      }
    },
    'primary-fiber': {
      label: 'Fabric', type: 'checkbox',
      display: {cotton:{label:'Cotton'},wool:{label:'Wool'},linen:{label:'Linen'},silk:{label:'Silk'},cashmere:{label:'Cashmere'},polyester:{label:'Polyester'},nylon:{label:'Nylon'},lycra:{label:'Lycra / Stretch'},viscose:{label:'Viscose'},tencel:{label:'Tencel'}}
    },
    season: {
      label: 'Season', type: 'checkbox', order: ['summer','winter','year-round'],
      display: {summer:{label:'Summer'},winter:{label:'Winter'},'year-round':{label:'Year-Round'}}
    },
    'weight-class': {
      label: 'Weight', type: 'checkbox', order: ['ultralight','lightweight','medium','heavyweight','super-heavy'],
      display: {ultralight:{label:'Ultralight (under 130 gsm)'},lightweight:{label:'Lightweight (130–200 gsm)'},medium:{label:'Medium (200–300 gsm)'},heavyweight:{label:'Heavyweight (300–400 gsm)'},'super-heavy':{label:'Super Heavy (400+ gsm)'}}
    },
    weave: {
      label: 'Weave', type: 'checkbox',
      display: {oxford:{label:'Oxford'},poplin:{label:'Poplin'},twill:{label:'Twill'},flannel:{label:'Flannel'},chambray:{label:'Chambray'},herringbone:{label:'Herringbone'},corduroy:{label:'Corduroy'},denim:{label:'Denim'},canvas:{label:'Canvas'},pinpoint:{label:'Pinpoint'},broadcloth:{label:'Broadcloth'},dobby:{label:'Dobby'},hopsack:{label:'Hopsack'},birdseye:{label:"Bird's Eye"},gabardine:{label:'Gabardine'},seersucker:{label:'Seersucker'},'end-on-end':{label:'End-on-End'},jacquard:{label:'Jacquard'},plain:{label:'Plain Weave'},tropical:{label:'Tropical'}}
    },
    pattern: {
      label: 'Pattern', type: 'checkbox',
      groups: {solid:{label:'Solid',tags:['solid']},stripes:{label:'Stripes',tags:['stripes','bengal-stripe','pin-stripe','pencil-stripe','candy-stripe','chalk-stripe','university-stripe']},checks:{label:'Checks',tags:['checks','gingham','tattersall','glen-check','windowpane','graph-check','gun-club','houndstooth']},plaid:{label:'Plaid / Tartan',tags:['plaid','tartan','madras']},herringbone:{label:'Herringbone',tags:['herringbone']},textured:{label:'Textured',tags:['textured','birdseye']},paisley:{label:'Paisley',tags:['paisley']},floral:{label:'Floral',tags:['floral']},geometric:{label:'Geometric',tags:['geometric']}}
    },
    formality: {
      label: 'Formality', type: 'checkbox', order: ['1','2','3','4','5'],
      display: {'1':{label:'① Casual'},'2':{label:'② Smart Casual'},'3':{label:'③ Business Casual'},'4':{label:'④ Business'},'5':{label:'⑤ Formal'}}
    },
    mill: {
      label: 'Mill / Brand', type: 'checkbox',
      display: {'dugdale':{label:'Dugdale Bros'},'vbc':{label:'Vitale Barberis Canonico'},'brisbane-moss':{label:'Brisbane Moss'},'thomas-mason':{label:'Thomas Mason'},'albini':{label:'Albini'},'canclini':{label:'Canclini'},'grandi-rubinelli':{label:'Grandi & Rubinelli'},'drago':{label:'Drago'},'brembana':{label:'Brembana'},'e-thomas':{label:'E. Thomas'},'huddersfield':{label:'Huddersfield'},'reda':{label:'Reda'},'zegna':{label:'Ermenegildo Zegna'},'loro-piana':{label:'Loro Piana'},'caccioppoli':{label:'Caccioppoli'},'solbiati':{label:'Solbiati'}}
    }
  };

  const TYPE_FILTERS = {
    pants:['color','primary-fiber','season','weight-class','weave','pattern','formality','mill'],
    shirt:['color','weave','pattern','formality','primary-fiber','season','mill'],
    jacket:['color','primary-fiber','pattern','season','formality','weave','mill'],
    suit:['color','primary-fiber','pattern','season','formality','weave','mill'],
    _default:['color','primary-fiber','season','weight-class','weave','pattern','formality','mill']
  };

  const SKIP_CATEGORIES = new Set(['UNKNOWN','composition','weight-gsm','formality-fabric','formality-style']);

  function buildGroupMaps(config) {
    const maps = {};
    for (const [cat, cfg] of Object.entries(config)) {
      if (!cfg.groups) continue;
      maps[cat] = {};
      for (const [groupKey, group] of Object.entries(cfg.groups)) {
        for (const tag of group.tags) maps[cat][tag] = groupKey;
      }
    }
    return maps;
  }
  const GROUP_MAPS = buildGroupMaps(FILTER_CONFIG);

  function resolveGroups(category, rawValues) {
    const map = GROUP_MAPS[category];
    if (!map) return rawValues;
    const result = new Set();
    for (const v of rawValues) result.add(map[v] || v);
    return [...result];
  }

  function extractAttributes(tags) {
    const attrs = {};
    for (const tag of tags) {
      if (!tag.startsWith('luxire:')) continue;
      const rest = tag.slice(7);
      const i = rest.indexOf(':');
      if (i === -1) continue;
      const category = rest.slice(0, i);
      if (SKIP_CATEGORIES.has(category)) continue;
      const value = rest.slice(i + 1);
      if (!attrs[category]) attrs[category] = [];
      if (!attrs[category].includes(value)) attrs[category].push(value);
    }
    return attrs;
  }

  function matchesFilters(product, filters, priceRange, inStockOnly, skipCategory) {
    for (const cat in filters) {
      if (cat === skipCategory) continue;
      const selected = filters[cat];
      if (!selected.length) continue;
      const values = resolveGroups(cat, product.attributes[cat] || []);
      if (!selected.some(v => values.includes(v))) return false;
    }
    if (priceRange.min !== null && product.price < priceRange.min) return false;
    if (priceRange.max !== null && product.price > priceRange.max) return false;
    if (inStockOnly && !product.available) return false;
    return true;
  }

  function sortByOrderThenCount(keys, order, counts) {
    return keys.slice().sort((a, b) => {
      const ai = order.indexOf(a), bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return (counts[b] || 0) - (counts[a] || 0);
    });
  }

  function titleCase(str) { return str.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); }
  function getFilterLabel(category, value) {
    const cfg = FILTER_CONFIG[category];
    if (!cfg) return titleCase(value);
    return cfg.display?.[value]?.label || cfg.groups?.[value]?.label || titleCase(value);
  }
  function formatMoney(cents) { return '$' + (cents / 100).toFixed(2); }

  const SVG = {
    check:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>',
    chevron:'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>',
    close:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    image:'<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>'
  };

  const _escDiv = document.createElement('div');
  function escapeHTML(text) { _escDiv.textContent = text; return _escDiv.innerHTML; }

  function parseProductCard(card) {
    const tags = (card.dataset.productTags || '').split(',').map(t => t.trim());
    return {
      element: card, id: card.dataset.productId, title: card.dataset.productTitle || '',
      url: card.getAttribute('href') || '', image: card.querySelector('img')?.src || '',
      vendor: card.dataset.productVendor || '',
      price: parseInt(card.dataset.productPrice) || 0,
      comparePrice: parseInt(card.dataset.productComparePrice) || 0,
      available: card.dataset.productAvailable === 'true',
      tags: tags, attributes: extractAttributes(tags), inDom: true
    };
  }

  function parseProductJSON(p) {
    const tags = typeof p.tags === 'string' ? p.tags.split(',').map(t => t.trim()) : (p.tags || []);
    const v = (p.variants && p.variants[0]) || {};
    const price = Math.round(parseFloat(v.price || p.price || '0') * 100);
    const comparePrice = Math.round(parseFloat(v.compare_at_price || p.compare_at_price || '0') * 100);
    const images = p.images || [];
    return {
      element: null, id: String(p.id), title: p.title || '',
      url: '/products/' + p.handle, image: images[0]?.src || '',
      vendor: p.vendor || '',
      price: price, comparePrice: comparePrice,
      available: p.available !== false && v.available !== false,
      tags: tags, attributes: extractAttributes(tags), inDom: false
    };
  }

  let dom = {};
  function cacheDom() {
    dom = {
      grid: document.getElementById('productGrid'),
      sidebar: document.getElementById('filtersSidebar'),
      form: document.getElementById('filtersForm'),
      priceMin: document.getElementById('priceMin'),
      priceMax: document.getElementById('priceMax'),
      inStockToggle: document.getElementById('inStockOnly'),
      visibleCount: document.getElementById('visibleCount'),
      resultCount: document.getElementById('resultCount'),
      totalCount: document.getElementById('totalCount'),
      emptyState: document.getElementById('emptyState'),
      activeSection: document.getElementById('activeFilters'),
      activeTags: document.getElementById('activeFilterTags'),
      activeNum: document.getElementById('activeFilterNum'),
      filterBadge: document.getElementById('activeFilterCount'),
      toolbarBadge: document.getElementById('toolbarBadge'),
      pagination: document.querySelector('.lx-pagination,.col-pagination'),
      sortSelect: document.getElementById('sortSelect')
    };
  }

  const Engine = {
    products: [], allProducts: [], activeFilters: {},
    priceRange: { min: null, max: null }, inStockOnly: false,
    isFullyLoaded: false, gridReplaced: false, totalProducts: 0,
    collectionHandle: '', collectionType: null,

    init() {
      if (!document.getElementById('productGrid')) return;
      cacheDom();
      this.totalProducts = parseInt(dom.grid.dataset.totalProducts) || 0;
      this.collectionHandle = dom.grid.dataset.collectionHandle || '';
      this.parseVisibleProducts();
      this.detectCollectionType();
      this.buildFilterSidebar();
      this.restoreFromURL();
      this.buildFilterOptions();
      this.bindEvents();
      this.applyFilters();
      this.loadFullCollection();
    },

    parseVisibleProducts() {
      const cards = dom.grid.querySelectorAll('[data-product-id]');
      this.products = Array.from(cards).map(parseProductCard);
      this.allProducts = this.products.slice();
      if (dom.totalCount) dom.totalCount.textContent = this.totalProducts;
    },

    detectCollectionType() {
      const h = this.collectionHandle.toLowerCase();
      if (/pant|trouser|chino|jean|gurkha/.test(h)) return (this.collectionType = 'pants');
      if (/shirt|oxford|chambray|linen-shirt/.test(h)) return (this.collectionType = 'shirt');
      if (/jacket|blazer|sport-coat/.test(h)) return (this.collectionType = 'jacket');
      if (/suit/.test(h)) return (this.collectionType = 'suit');
      const counts = {};
      for (const p of this.products) for (const t of (p.attributes.type || [])) counts[t] = (counts[t] || 0) + 1;
      const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      if (top && top[1] > this.products.length * 0.6) this.collectionType = top[0];
    },

    async loadFullCollection() {
      if (this.totalProducts <= 48) { this.isFullyLoaded = true; this.buildFilterOptions(); return; }
      const loadedIds = new Set(this.allProducts.map(p => p.id));
      const maxPages = Math.ceil(this.totalProducts / 250);
      for (let page = 1; page <= maxPages; page++) {
        try {
          const url = '/collections/' + this.collectionHandle + '/products.json?limit=250&page=' + page;
          const resp = await fetch(url);
          if (!resp.ok) break;
          const products = (await resp.json()).products || [];
          for (const p of products) { const id = String(p.id); if (!loadedIds.has(id)) { this.allProducts.push(parseProductJSON(p)); loadedIds.add(id); } }
          if (products.length < 250) break;
        } catch (err) { console.warn('[Luxire] Failed page', page, err); break; }
      }
      this.isFullyLoaded = true;
      this.buildFilterOptions();
      this.applyFilters();
    },

    buildFilterSidebar() {
      if (!dom.form) return;
      const filterOrder = TYPE_FILTERS[this.collectionType] || TYPE_FILTERS._default;
      const staticGroups = dom.form.querySelectorAll('[data-filter-group]:not([data-filter-category])');
      const availGroup = staticGroups.length > 1 ? staticGroups[staticGroups.length - 1] : null;
      dom.form.querySelectorAll('[data-filter-category]').forEach(g => g.remove());
      for (const category of filterOrder) {
        const config = FILTER_CONFIG[category]; if (!config) continue;
        const collapsed = category === 'mill' || category === 'formality';
        const listTag = config.type === 'color' ? '<div class="lx-filter-colors" data-filter-list="' + category + '"></div>' : '<ul class="lx-filter-options" data-filter-list="' + category + '"></ul>';
        const group = document.createElement('div');
        group.className = 'lx-filter-group';
        group.setAttribute('data-filter-group', '');
        group.setAttribute('data-filter-category', category);
        const loading = '<span class="lx-filter-loading" data-filter-loading><span class="lx-filter-loading__bar"></span><span class="lx-filter-loading__bar"></span><span class="lx-filter-loading__bar"></span><span class="lx-filter-loading__bar"></span></span>';
        group.innerHTML = '<button type="button" class="lx-filter-group__header" aria-expanded="' + !collapsed + '" data-filter-toggle><span class="lx-filter-group__title">' + config.label + ' <span class="lx-filter-group__badge" data-badge style="display:none">0</span>' + loading + '</span>' + SVG.chevron + '</button><div class="lx-filter-group__content">' + listTag + '</div>';
        dom.form.insertBefore(group, availGroup);
      }
    },

    buildFilterOptions() {
      const pool = this.isFullyLoaded ? this.allProducts : this.products;
      const allCounts = {};
      for (const p of pool) for (const [cat, values] of Object.entries(p.attributes)) { if (!allCounts[cat]) allCounts[cat] = {}; for (const val of resolveGroups(cat, values)) allCounts[cat][val] = (allCounts[cat][val] || 0) + 1; }
      for (const [category, config] of Object.entries(FILTER_CONFIG)) {
        const container = document.querySelector('[data-filter-list="' + category + '"]'); if (!container) continue;
        const counts = allCounts[category] || {};
        if (config.type === 'color') this._renderColorSwatches(container, category, config, counts);
        else this._renderCheckboxes(container, category, config, counts);
      }
      if (this.isFullyLoaded && dom.form) dom.form.classList.add('lx-filters--ready');
    },

    _renderColorSwatches(container, category, config, counts) {
      const order = config.order || Object.keys(config.display);
      const keys = sortByOrderThenCount(Object.keys(counts).filter(k => counts[k] > 0 && config.display[k]), order, counts);
      const show = this.isFullyLoaded;
      container.innerHTML = keys.map(key => {
        const d = config.display[key] || {label:titleCase(key),hex:'#ccc'};
        const sel = (this.activeFilters[category] || []).includes(key);
        const cc = (d.hex === '#f5f5f0' || d.hex === '#ffffff') ? '#333' : '#fff';
        const cs = show ? ' (' + counts[key] + ')' : '';
        return '<button type="button" class="lx-filter-color' + (sel ? ' is-selected' : '') + '" data-filter-category="' + category + '" data-filter-value="' + key + '" data-color="' + key + '" style="background:' + (d.hex||'#ccc') + (d.border ? ';box-shadow:inset 0 0 0 1px rgba(0,0,0,.15)' : '') + '" title="' + d.label + cs + '"><span class="lx-filter-color__check"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="' + cc + '" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg></span></button>';
      }).join('');
    },

    _renderCheckboxes(container, category, config, counts) {
      const source = config.groups || config.display || {};
      const order = config.order || Object.keys(source);
      const keys = sortByOrderThenCount(Object.keys(source).filter(k => counts[k] > 0), order, counts);
      const show = this.isFullyLoaded;
      container.innerHTML = keys.map(key => {
        const label = source[key]?.label || titleCase(key);
        const checked = (this.activeFilters[category] || []).includes(key);
        const cs = show ? '(' + counts[key] + ')' : '';
        return '<li class="lx-filter-option"><label><input type="checkbox" data-filter-category="' + category + '" data-filter-value="' + key + '" ' + (checked ? 'checked' : '') + '><span class="lx-filter-checkbox">' + SVG.check + '</span><span class="lx-filter-label">' + label + '</span><span class="lx-filter-count">' + cs + '</span></label></li>';
      }).join('');
    },

    applyFilters() {
      const hasActive = this.hasActiveFilters();
      const pool = (this.isFullyLoaded && hasActive) ? this.allProducts : this.products;
      const filtered = pool.filter(p => matchesFilters(p, this.activeFilters, this.priceRange, this.inStockOnly, null));
      if (hasActive && this.isFullyLoaded) this.renderFilteredGrid(filtered);
      else if (this.gridReplaced) this.restoreOriginalGrid();
      else { const ids = new Set(filtered.map(p => p.id)); for (const p of this.products) if (p.element) p.element.classList.toggle('is-hidden', !ids.has(p.id)); }
      this.updateUI(filtered.length);
      this.updateFacetedCounts();
    },

    hasActiveFilters() {
      for (const cat in this.activeFilters) if (this.activeFilters[cat].length) return true;
      return this.priceRange.min !== null || this.priceRange.max !== null || this.inStockOnly;
    },

    updateFacetedCounts() {
      if (!this.isFullyLoaded) return;
      const hasActive = this.hasActiveFilters();
      for (const category of Object.keys(FILTER_CONFIG)) {
        const catPool = hasActive ? this.allProducts.filter(p => matchesFilters(p, this.activeFilters, this.priceRange, this.inStockOnly, category)) : this.allProducts;
        const counts = {};
        for (const p of catPool) for (const val of resolveGroups(category, p.attributes[category] || [])) counts[val] = (counts[val] || 0) + 1;
        const list = document.querySelector('[data-filter-list="' + category + '"]'); if (!list) continue;
        const activeVals = new Set(this.activeFilters[category] || []);
        if (FILTER_CONFIG[category].type === 'color') {
          list.querySelectorAll('.lx-filter-color').forEach(sw => { const val = sw.dataset.filterValue; const count = counts[val] || 0; const d = FILTER_CONFIG[category]?.display?.[val]; if (d) sw.title = d.label + ' (' + count + ')'; sw.classList.toggle('is-unavailable', count === 0 && !activeVals.has(val)); });
          list.querySelectorAll('.lx-filter-color.is-unavailable').forEach(sw => list.appendChild(sw));
        } else {
          list.querySelectorAll('.lx-filter-option').forEach(li => { const input = li.querySelector('input[data-filter-value]'); const countEl = li.querySelector('.lx-filter-count'); if (!input) return; const val = input.dataset.filterValue; const count = counts[val] || 0; if (countEl) countEl.textContent = '(' + count + ')'; li.classList.toggle('is-unavailable', count === 0 && !activeVals.has(val)); });
          list.querySelectorAll('.lx-filter-option.is-unavailable').forEach(li => list.appendChild(li));
        }
      }
    },

    renderFilteredGrid(filtered) {
      if (dom.pagination) dom.pagination.style.display = 'none';
      const frag = document.createDocumentFragment();
      const visible = filtered.slice(0, 200);
      for (const p of visible) frag.appendChild(this.createProductCard(p));
      dom.grid.innerHTML = '';
      dom.grid.appendChild(frag);
      this.gridReplaced = true;
      if (filtered.length > 200) { const n = document.createElement('div'); n.className = 'lx-filter-notice'; n.textContent = 'Showing first 200 of ' + filtered.length + ' results.'; dom.grid.appendChild(n); }
    },

    restoreOriginalGrid() {
      dom.grid.innerHTML = '';
      const frag = document.createDocumentFragment();
      for (const p of this.products) { if (p.element) { p.element.classList.remove('is-hidden'); frag.appendChild(p.element); } }
      dom.grid.appendChild(frag);
      this.gridReplaced = false;
      if (dom.pagination) dom.pagination.style.display = '';
    },

    createProductCard(product) {
      const card = document.createElement('a');
      card.className = 'pcard lx-product-card';
      card.href = product.url;
      card.dataset.productId = product.id;
      const discount = product.comparePrice > product.price ? Math.round((product.comparePrice - product.price) / product.comparePrice * 100) : 0;
      const title = escapeHTML(product.title);
      card.innerHTML = '<div class="pcard-imgwrap">' + (product.image ? '<img class="pcard-img" src="' + product.image + '" alt="' + title + '" loading="lazy">' : '<div class="pcard-img" style="background:#eee"></div>') + (discount > 0 ? '<span class="pcard-badge" style="background:#c41e3a">-' + discount + '%</span>' : '') + '</div><div class="pcard-body"><div class="pcard-name">' + title + '</div><div class="pcard-price"><span class="pcard-price-now">' + formatMoney(product.price) + '</span>' + (discount > 0 ? '<span class="pcard-price-was">' + formatMoney(product.comparePrice) + '</span><span class="pcard-price-save">-' + discount + '%</span>' : '') + '</div></div>';
      return card;
    },

    updateUI(visibleCount) {
      if (dom.visibleCount) dom.visibleCount.textContent = visibleCount;
      if (dom.resultCount) dom.resultCount.textContent = visibleCount;
      if (dom.emptyState) dom.emptyState.style.display = visibleCount === 0 ? 'block' : 'none';
      if (dom.grid) dom.grid.style.display = visibleCount === 0 ? 'none' : '';
      let total = 0;
      for (const cat in this.activeFilters) total += this.activeFilters[cat].length;
      if (this.priceRange.min !== null || this.priceRange.max !== null) total++;
      if (this.inStockOnly) total++;
      if (dom.filterBadge) { dom.filterBadge.textContent = total; dom.filterBadge.style.display = total > 0 ? '' : 'none'; }
      if (dom.toolbarBadge) { dom.toolbarBadge.textContent = total; dom.toolbarBadge.style.display = total > 0 ? '' : 'none'; }
      document.querySelectorAll('.lx-filter-group[data-filter-category]').forEach(group => { const badge = group.querySelector('[data-badge]'); if (!badge) return; const count = (this.activeFilters[group.dataset.filterCategory] || []).length; badge.textContent = count; badge.style.display = count > 0 ? '' : 'none'; });
      this.renderActiveTags(total);
    },

    renderActiveTags(total) {
      if (!dom.activeSection) return;
      if (total === 0) { dom.activeSection.style.display = 'none'; dom.activeNum.textContent = '0'; dom.activeTags.innerHTML = ''; return; }
      dom.activeSection.style.display = '';
      dom.activeNum.textContent = total;
      const parts = [];
      for (const cat in this.activeFilters) for (const val of this.activeFilters[cat]) parts.push('<button type="button" class="lx-filter-tag" data-category="' + cat + '" data-value="' + val + '">' + getFilterLabel(cat, val) + ' ' + SVG.close + '</button>');
      if (this.priceRange.min !== null || this.priceRange.max !== null) { const min = this.priceRange.min ? '$' + (this.priceRange.min / 100) : '$0'; const max = this.priceRange.max ? '$' + (this.priceRange.max / 100) : '+'; parts.push('<button type="button" class="lx-filter-tag" data-category="price" data-value="range">' + min + ' – ' + max + ' ' + SVG.close + '</button>'); }
      if (this.inStockOnly) parts.push('<button type="button" class="lx-filter-tag" data-category="inStock" data-value="true">In Stock Only ' + SVG.close + '</button>');
      dom.activeTags.innerHTML = parts.join('');
    },

    saveToURL() {
      const url = new URL(window.location);
      [...url.searchParams.keys()].filter(k => k.startsWith('f.') || k === 'price' || k === 'stock').forEach(k => url.searchParams.delete(k));
      for (const cat in this.activeFilters) if (this.activeFilters[cat].length) url.searchParams.set('f.' + cat, this.activeFilters[cat].join(','));
      if (this.priceRange.min !== null || this.priceRange.max !== null) url.searchParams.set('price', (this.priceRange.min ? this.priceRange.min / 100 : '') + '-' + (this.priceRange.max ? this.priceRange.max / 100 : ''));
      if (this.inStockOnly) url.searchParams.set('stock', '1');
      history.pushState(null, '', url);
    },

    restoreFromURL() {
      const url = new URL(window.location);
      for (const [key, value] of url.searchParams) if (key.startsWith('f.')) this.activeFilters[key.slice(2)] = value.split(',').filter(Boolean);
      const pp = url.searchParams.get('price');
      if (pp) { const [min, max] = pp.split('-'); if (min) { this.priceRange.min = parseInt(min) * 100; if (dom.priceMin) dom.priceMin.value = min; } if (max) { this.priceRange.max = parseInt(max) * 100; if (dom.priceMax) dom.priceMax.value = max; } }
      if (url.searchParams.get('stock') === '1') { this.inStockOnly = true; if (dom.inStockToggle) dom.inStockToggle.checked = true; }
    },

    clearAllFilters() {
      this.activeFilters = {}; this.priceRange = {min:null,max:null}; this.inStockOnly = false;
      document.querySelectorAll('input[data-filter-category]').forEach(el => el.checked = false);
      document.querySelectorAll('.lx-filter-color').forEach(el => el.classList.remove('is-selected'));
      if (dom.priceMin) dom.priceMin.value = '';
      if (dom.priceMax) dom.priceMax.value = '';
      document.querySelectorAll('[data-price-preset]').forEach(b => b.classList.remove('is-active'));
      if (dom.inStockToggle) dom.inStockToggle.checked = false;
      if (dom.pagination) dom.pagination.style.display = '';
      this.applyFilters(); this.saveToURL();
    },

    bindEvents() {
      // Mobile drawer
      document.getElementById('filterOpen')?.addEventListener('click', () => { dom.sidebar.classList.add('is-open'); document.body.classList.add('is-scroll-locked'); });
      document.getElementById('filterClose')?.addEventListener('click', () => this._closeDrawer());
      document.getElementById('filterOverlay')?.addEventListener('click', () => this._closeDrawer());
      document.getElementById('applyFilters')?.addEventListener('click', () => this._closeDrawer());
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this._closeDrawer(); });

      // Accordion
      document.addEventListener('click', e => { const t = e.target.closest('[data-filter-toggle]'); if (t) { const exp = t.getAttribute('aria-expanded') === 'true'; t.setAttribute('aria-expanded', !exp); } });

      // Checkboxes
      document.addEventListener('change', e => {
        if (!e.target.matches('input[data-filter-category]')) return;
        const {filterCategory:cat, filterValue:val} = e.target.dataset;
        if (!this.activeFilters[cat]) this.activeFilters[cat] = [];
        if (e.target.checked) { if (!this.activeFilters[cat].includes(val)) this.activeFilters[cat].push(val); }
        else this.activeFilters[cat] = this.activeFilters[cat].filter(v => v !== val);
        this.applyFilters(); this.saveToURL();
      });

      // Color swatches
      document.addEventListener('click', e => {
        const sw = e.target.closest('.lx-filter-color'); if (!sw) return;
        const {filterCategory:cat, filterValue:val} = sw.dataset;
        if (!this.activeFilters[cat]) this.activeFilters[cat] = [];
        if (sw.classList.toggle('is-selected')) this.activeFilters[cat].push(val);
        else this.activeFilters[cat] = this.activeFilters[cat].filter(v => v !== val);
        this.applyFilters(); this.saveToURL();
      });

      // Price
      let timer;
      const handlePrice = () => { clearTimeout(timer); timer = setTimeout(() => { this.priceRange.min = dom.priceMin?.value ? parseInt(dom.priceMin.value) * 100 : null; this.priceRange.max = dom.priceMax?.value ? parseInt(dom.priceMax.value) * 100 : null; this.applyFilters(); this.saveToURL(); }, 400); };
      dom.priceMin?.addEventListener('input', handlePrice);
      dom.priceMax?.addEventListener('input', handlePrice);
      document.querySelectorAll('[data-price-preset]').forEach(btn => { btn.addEventListener('click', function() { const [min,max] = this.dataset.pricePreset.split('-'); if(dom.priceMin)dom.priceMin.value=min||''; if(dom.priceMax)dom.priceMax.value=max||''; document.querySelectorAll('[data-price-preset]').forEach(b=>b.classList.remove('is-active')); this.classList.add('is-active'); handlePrice(); }); });

      // Stock
      dom.inStockToggle?.addEventListener('change', e => { this.inStockOnly = e.target.checked; this.applyFilters(); this.saveToURL(); });

      // Clear
      document.getElementById('clearAllFilters')?.addEventListener('click', () => this.clearAllFilters());
      document.getElementById('emptyStateClear')?.addEventListener('click', () => this.clearAllFilters());

      // Active tag removal
      dom.activeTags?.addEventListener('click', e => {
        const tag = e.target.closest('.lx-filter-tag'); if (!tag) return;
        const {category:cat, value:val} = tag.dataset;
        if (cat === 'price') { if(dom.priceMin)dom.priceMin.value=''; if(dom.priceMax)dom.priceMax.value=''; this.priceRange={min:null,max:null}; document.querySelectorAll('[data-price-preset]').forEach(b=>b.classList.remove('is-active')); }
        else if (cat === 'inStock') { if(dom.inStockToggle)dom.inStockToggle.checked=false; this.inStockOnly=false; }
        else { this.activeFilters[cat]=(this.activeFilters[cat]||[]).filter(v=>v!==val); const cb=document.querySelector('input[data-filter-category="'+cat+'"][data-filter-value="'+val+'"]'); if(cb)cb.checked=false; const sw=document.querySelector('.lx-filter-color[data-filter-category="'+cat+'"][data-filter-value="'+val+'"]'); if(sw)sw.classList.remove('is-selected'); }
        this.applyFilters(); this.saveToURL();
      });

      // Sort
      dom.sortSelect?.addEventListener('change', e => { const u = new URL(window.location); u.searchParams.set('sort_by', e.target.value); window.location = u; });

      // Popstate
      window.addEventListener('popstate', () => {
        this.activeFilters={}; this.priceRange={min:null,max:null}; this.inStockOnly=false;
        document.querySelectorAll('input[data-filter-category]').forEach(el=>el.checked=false);
        document.querySelectorAll('.lx-filter-color').forEach(el=>el.classList.remove('is-selected'));
        if(dom.priceMin)dom.priceMin.value=''; if(dom.priceMax)dom.priceMax.value='';
        document.querySelectorAll('[data-price-preset]').forEach(b=>b.classList.remove('is-active'));
        if(dom.inStockToggle)dom.inStockToggle.checked=false;
        this.restoreFromURL(); this.buildFilterOptions(); this.applyFilters();
      });
    },

    _closeDrawer() {
      if (!dom.sidebar?.classList.contains('is-open')) return;
      dom.sidebar.classList.remove('is-open');
      document.body.classList.remove('is-scroll-locked');
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => Engine.init());
  else Engine.init();
})();
